import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMXN, fmtTime, elapsedMin, ordenActiva, entregaCompleta } from "@/lib/api";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import OrderStateBadge from "@/components/OrderStateBadge";
import { Plus, Sandwich, Clock, ShoppingBag } from "lucide-react";

export default function MeseroHome() {
  const navigate = useNavigate();
  const [mesas, setMesas] = useState([]);
  const [ordenes, setOrdenes] = useState([]);

  const load = async () => {
    const [m, o] = await Promise.all([
      api.get("/mesas"),
      api.get("/ordenes", { params: { only_today: true, limit: 200 } }),
    ]);
    setMesas(m.data);
    setOrdenes(o.data.items || []);
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  useRealtimeEvents((msg) => { if (msg?.type?.startsWith("orden.")) load(); }, () => load());

  // Lógica central en /lib/api: ordenActiva = NO finalizada (cobrada+entregada).
  // Esto cubre TODOS los casos:
  //   - Entregada pero no cobrada → activa (caja debe poder cobrar)
  //   - Cobrada pero no entregada → activa (mesero debe poder entregar)
  //   - Cobrada y entregada → finalizada, mesa libre.
  const ordenPorMesa = (mesaId) => ordenes.find((o) => o.mesa_id === mesaId && ordenActiva(o));
  const ordenesLlevar = ordenes.filter((o) => o.tipo !== "MESA" && ordenActiva(o));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Salón</h2>
          <p className="text-sm text-muted-foreground mt-1">Toma órdenes y consulta el estado de cada mesa.</p>
        </div>
        <Button size="lg" className="h-12 font-bold" onClick={() => navigate("/mesero/nueva")} data-testid="mesero-nueva-orden-btn">
          <Plus className="h-5 w-5 mr-2" />Nueva orden
        </Button>
      </div>

      <div>
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><Sandwich className="h-4 w-4" /> Mesas</h3>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {mesas.map((m) => {
            const o = ordenPorMesa(m.id);
            return (
              <Card key={m.id} className={o ? "border-primary" : ""} data-testid={`mesa-tile-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="text-2xl font-black">{m.nombre}</div>
                    {o && <OrderStateBadge state={o.estado} />}
                  </div>
                  {o && o.cobrada && !entregaCompleta(o) && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100" data-testid={`mesa-pendiente-entrega-${m.id}`}>
                      ⏳ Entrega pendiente
                    </div>
                  )}
                  {o && !o.cobrada && entregaCompleta(o) && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-violet-200 dark:bg-violet-900/60 text-violet-900 dark:text-violet-100" data-testid={`mesa-pendiente-cobro-${m.id}`}>
                      💰 Pendiente de cobro
                    </div>
                  )}
                  {o ? (
                    <>
                      <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-testid={`mesa-numero-${m.id}`}>
                        {o.numero}
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        {o.items.length} producto(s) · hace {elapsedMin(o.created_at)} min
                      </div>
                      {o.created_by_nombre && (
                        <div className="text-xs text-muted-foreground truncate" title={o.created_by_nombre}>
                          Mesero: <span className="font-semibold text-foreground/80">{o.created_by_nombre}</span>
                        </div>
                      )}
                      <div className="mt-1 text-xl font-bold text-primary">{fmtMXN(o.total)}</div>
                      <Button className="w-full mt-3 h-11" variant="outline" onClick={() => navigate(`/mesero/orden/${o.id}`)} data-testid={`abrir-mesa-${m.id}`}>
                        Abrir orden
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="mt-3 text-xs text-muted-foreground">Libre</div>
                      <Button className="w-full mt-3 h-11" onClick={() => navigate(`/mesero/nueva?mesa=${m.id}`)} data-testid={`abrir-mesa-libre-${m.id}`}>
                        <Plus className="h-4 w-4 mr-1" />Tomar orden
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> Para llevar / Domicilio activos</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ordenesLlevar.map((o) => (
            <Card key={o.id} className="cursor-pointer hover:border-primary" onClick={() => navigate(`/mesero/orden/${o.id}`)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-black">{o.numero}</div>
                  <OrderStateBadge state={o.estado} />
                </div>
                {o.cobrada && !entregaCompleta(o) && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100" data-testid={`llevar-pendiente-entrega-${o.id}`}>
                    ⏳ Entrega pendiente
                  </div>
                )}
                {!o.cobrada && entregaCompleta(o) && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-violet-200 dark:bg-violet-900/60 text-violet-900 dark:text-violet-100" data-testid={`llevar-pendiente-cobro-${o.id}`}>
                    💰 Pendiente de cobro
                  </div>
                )}
                <div className="mt-2 text-sm">{o.cliente_nombre}</div>
                <div className="text-xs text-muted-foreground">{o.cliente_telefono} · {o.entrega_tipo}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-lg font-bold text-primary">{fmtMXN(o.total)}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{fmtTime(o.created_at)}</div>
                </div>
              </CardContent>
            </Card>
          ))}
          {ordenesLlevar.length === 0 && <div className="col-span-full text-sm text-muted-foreground">Sin pedidos para llevar activos.</div>}
        </div>
      </div>
    </div>
  );
}
